import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import Hashids from 'hashids';
import moment from 'moment-timezone';
import { CompleteEvent, FunctionFactory, Model, surveyLocalization } from 'survey-core';
import { Survey as SurveyComponent } from 'survey-react-ui';
import { initializeSurveyForBrand } from '@latitude-digital/survey-core';
import { Button } from '@latitude-digital/survey-core/fds';

import { useShowContext } from '@/contexts/ShowContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { prepareForSurvey, prepareSurveyOnQuestionAdded } from '@/helpers/surveyTemplatesAll';
import { supportedLocalesFor } from '@/pages/layout';
import SurveyStepper from '@/components/SurveyStepper';
import { buildAbandonedPayload, buildSurveyPayload, countMinors } from '@/lib/apiPayload';
import { applyWaiverGates, requiredWaiverShowTypes, resolveWaiverGates } from '@/lib/waivers';
import '@/styles/survey-page.css';

import fordUS from './ford_us.json';
import fordCA from './ford_ca.json';
import lincolnUS from './lincoln_us.json';
import lincolnCA from './lincoln_ca.json';

import { v4 as uuidv4 } from 'uuid';
import surveyService from '@/services/surveyService';
import { useTranslation } from '@/hooks/useTranslation';
import { AxiosError } from 'axios';

// Helper function to convert string errors to AxiosError format
const createError = (message: string): AxiosError => {
  return new AxiosError(message, 'ERR_CUSTOM', undefined, undefined, undefined);
};

function validateEmail(this: {
  question: {
    value: string;
    setPropertyValue: (key: string, value: string) => void;
    didYouMean?: string;
  };
  survey: any;
  returnResult: (valid?: boolean) => void;
}) {
  const email = this.question.value;

  this.question.setPropertyValue('didYouMean', '');

  if (!email) {
    this.returnResult();
    return;
  }

  fetch('https://us-central1-latitude-lead-system.cloudfunctions.net/validateEmail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
    .then(response => {
      response.json().then(res => {
        const { results } = res;

        let valid = true;

        // bad emails are rejected
        if (results?.valid === false) {
          valid = false;
        }

        // disposable email services are rejected
        if (results?.is_disposable === true) {
          valid = false;
        }

        // reject delivery_confidence below 20
        if (results?.delivery_confidence < 20) {
          valid = false;
        }

        // typos are rejected with correction
        if (results.did_you_mean) {
          valid = true;
          this.question.setPropertyValue('didYouMean', results.did_you_mean);
        }

        this.returnResult(valid);
      });
    })
    .catch(err => {
      // Convert string error to AxiosError
      const axiosError = createError(err.toString());
      alert(axiosError.message);
    });
}

if (!FunctionFactory.Instance.hasFunction('validateEmail')) {
  FunctionFactory.Instance.register('validateEmail', validateEmail, true);
}

const extractUTM = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);

  const utm: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].includes(key)) {
      utm[key] = value;
    }
  }

  return utm;
};

const SurveySkeleton = () => (
  <div className="survey-skeleton" role="status" aria-label="Loading">
    <div className="survey-skeleton__bar" style={{ width: '40%' }} />
    <div className="survey-skeleton__bar" style={{ width: '25%' }} />
    <div className="survey-skeleton__bar survey-skeleton__bar--input" />
    <div className="survey-skeleton__bar" style={{ width: '25%' }} />
    <div className="survey-skeleton__bar survey-skeleton__bar--input" />
    <div className="survey-skeleton__bar" style={{ width: '30%' }} />
    <div className="survey-skeleton__bar survey-skeleton__bar--input" />
    <div className="survey-skeleton__bar" style={{ width: '30%' }} />
    <div className="survey-skeleton__bar survey-skeleton__bar--input" />
  </div>
);

const Survey = () => {
  const { _hashed_show_id, specialist_id, timestamp } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, setLanguage } = useLanguage();
  const [thisSurvey, setThisSurvey] = useState<Model>();
  const source = searchParams.get('t') || 'microsite';
  const { showData, loading, error } = useShowContext();
  const [currentPage, setCurrentPage] = useState(0);
  const { t } = useTranslation();
  const [resolvedConfig, setResolvedConfig] = useState<any>(null);
  const [consentRejected, setConsentRejected] = useState(false);
  // Bumped after `survey.locale` changes so localized page titles re-render (value itself unused).
  const [, setLocaleVersion] = useState(0);

  // Handle language selection based on priority
  useEffect(() => {
    if (!showData) return;

    const availableLanguages = supportedLocalesFor(showData.isCanadian);

    // Priority 1: URL parameter (only if it's a supported language for this show type)
    const urlLang = searchParams.get('lang');
    if (urlLang && availableLanguages.includes(urlLang)) {
      setLanguage(urlLang);
      return;
    }

    // Priority 2: Browser language (only if it's a supported language for this show type)
    const browserLang = navigator.language.split('-')[0];
    if (browserLang && availableLanguages.includes(browserLang)) {
      setLanguage(browserLang);
      return;
    }

    // Priority 3: Default to English
    setLanguage('en');
  }, [searchParams, showData, setLanguage]);

  surveyLocalization.defaultLocale = 'en';

  // Decode and validate QR timestamp
  const hasher = new Hashids('stupid scammers');
  const [ts] = hasher.decode(timestamp!);
  const _qrTime = moment(Number(ts));

  // Create memoized survey config outside initSurvey
  const surveyConfig = useMemo(async () => {
    if (!showData) return null;

    // Set supported locales (the dictionaries come from survey-core/i18n,
    // loaded in src/lib/surveyBootstrap.ts)
    surveyLocalization.supportedLocales = supportedLocalesFor(showData.isCanadian);

    // Set document properties
    document.title = showData.showName;

    const favicon = showData.isLincoln
      ? 'https://cdn.metinc.tech/logos/Lincoln_favicon.png'
      : 'https://cdn.metinc.tech/logos/Ford_favicon.png';
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (link) link.href = favicon;

    // Create survey config
    const surveyJSON = showData.isCanadian
      ? showData.isLincoln
        ? lincolnCA
        : fordCA
      : showData.isLincoln
        ? lincolnUS
        : fordUS;

    // Deep copy: the injector mutates panel element arrays and the imported JSON must stay pristine.
    const config = structuredClone(surveyJSON) as Record<string, any>;

    // Waiver-required activations: one sanitised markdown block per waiver the
    // show offers (visitor's own activation first) above the shared signature.
    // getWaiverSet() normalizes all three raw shapes (per-activation, hybrid
    // general+per-activation, legacy general-only) into the map injectWaivers
    // expects — see AutoShow.getWaiverSet (SA-1671).
    //
    // The per-side gates come back from the same call that did the injecting: whether there is
    // anything actually readable to show, per side — absent, malformed, empty-string and
    // non-string waiver data all inject zero questions for that side, so that side is absent
    // everywhere (resolveWaiverGates / normalizeWaiverText in lib/waivers.ts). The sides are
    // gated independently because the general signature/agreement pair only attests to ADULT
    // content, so it must depend on adult content specifically, never on minor content alone —
    // otherwise a minor-only show lets a visitor who has no minors sign over zero visible adult
    // text (the SA-1671 failure this exists to stop). Minor content is symmetrically gated on
    // its own flag, on top of the existing minorsYesNo answer, so a "yes minors" answer never
    // demands minorSignature over an empty minorWaiversPanel either.
    const waiverGates = resolveWaiverGates(config, showData.getWaiverSet(source), source);

    return { config, waiverGates };
  }, [showData, source]);

  useEffect(() => {
    surveyConfig?.then(config => setResolvedConfig(config));
  }, [surveyConfig]);

  // Create survey instance
  useEffect(() => {
    if (showData && resolvedConfig) {
      const initSurvey = async () => {
        surveyLocalization.supportedLocales = supportedLocalesFor(showData.isCanadian);

        const survey = new Model(resolvedConfig.config);
        // Model-only setup: applies the FDS theme for the brand and appends
        // the `meridian-survey` marker class the token adapter keys off.
        // Runtime opt-ins: validate while typing, errors below every control,
        // and the branded localized "Submit" label on the last page.
        initializeSurveyForBrand(survey, {
          brand: showData.isLincoln ? 'Lincoln' : 'Ford',
          textUpdateMode: true,
          questionErrorLocation: 'bottom',
          completeText: 'submit',
        });

        // SA-1689: SurveyJS's serializer already defaults `questionDescriptionLocation` to
        // 'underTitle' (survey-core 3.0.3, verified: descriptions rendered before this line
        // existed), so this assignment documents that default rather than enabling it.
        // Presence of a `description` in the JSON is what decides whether a question shows one,
        // EXCEPT `voi`: `@latitude-digital/survey-core`'s `hideVoiCompositeDescription()`
        // overrides its `getDescriptionLocation()` to `'hidden'` unconditionally (Ford and
        // Lincoln), so an authored `voi` description would never render regardless of this
        // setting. (Per-question `descriptionLocation` overrides, e.g. phone's `underInput`,
        // still win.)
        survey.questionDescriptionLocation = 'underTitle';

        // The page renders the current page's title as its own <h1> (design);
        // SurveyJS must not repeat it inside the survey root.
        survey.showPageTitles = false;

        // Set critical values immediately for conditional logic
        survey.setValue('source', source);
        const needsWaivers = requiredWaiverShowTypes.includes(source);
        survey.setValue('showWaivers', needsWaivers);
        // Seeds `waiverContentAvailable` (normal waiver-signing content vs the "unavailable"
        // fallback message — see ford_us.json etc.) plus the independent per-side gates the
        // signature questions' `visibleIf` expressions read. All three come from what
        // injectWaivers actually rendered, not from the activation type (SA-1671).
        applyWaiverGates(survey, resolvedConfig.waiverGates);
        if (showData.isCanadian) {
          survey.setValue('country', 'ca');
        }

        // Override address field labels for Canadian events
        if (showData.isCanadian) {
          const addressGroup = survey.getQuestionByName('address_group');

          if (addressGroup && addressGroup.contentPanel) {
            // Access the questions array directly
            const questions = addressGroup.contentPanel.questions || addressGroup.contentPanel.elements;

            if (questions) {
              questions.forEach((question: any) => {
                if (question.name === 'state') {
                  question.locTitle.setJson({
                    en: 'Province',
                    fr: 'Province',
                  });
                  question.maxLength = 2;
                }

                if (question.name === 'zip') {
                  question.locTitle.setJson({
                    en: 'Postal Code',
                    fr: 'Code Postal',
                  });
                  // Clear the US-only zip validators — Canadian format validated below
                  question.validators = [];
                }
              });
            }
          }
        }

        // Set up event handlers immediately after creating the survey
        prepareForSurvey(survey);
        prepareSurveyOnQuestionAdded(null, { survey });

        if (showData.isCanadian) {
          survey.onValidateQuestion.add((_s, options) => {
            if (options.question.name === 'zip' && options.value) {
              const canadianPostal = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
              const usZip = /^\d{5}(?:-\d{4})?$/;
              if (!canadianPostal.test(options.value) && !usZip.test(options.value)) {
                options.error = survey.locale === 'fr' ? 'Code postal invalide' : 'Invalid Postal Code';
              }
            }
          });
        }

        survey.onAfterRenderSurvey.add((sender: any) => {
          sender.setValue('device_survey_guid', uuidv4());
          sender.setValue('start_time', new Date());
          sender.setValue('survey_date', new Date());
          sender.setValue('show_id', showData.showID);
          sender.setValue('specialist_id', specialist_id || '');
          sender.setValue('app_version', 'microssite 2.0');
          sender.setValue('abandoned', 0);
          sender.setValue('device_id', window.navigator?.userAgent || '');

          sender.setValue('_utm', extractUTM());
          sender.setValue('_referrer', window.frames?.top?.document?.referrer);
          sender.setValue('_language', window.navigator?.language);
          sender.setValue('_screenWidth', window.screen?.width);
          sender.setValue('_offset', new Date().getTimezoneOffset());
          sender.setValue('_timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);
          sender.setValue('isGlobalShow', showData.isGlobalShow);

          const vehicleID = searchParams.get('vehicleID');
          if (vehicleID) {
            sender.setValue('voi', [Number(vehicleID)]);
          }
        });

        survey.onComplete.add(async (sender: Model, options: CompleteEvent) => {
          const originalMesage = sender.completedHtml;
          sender.completedHtml = '<h3>Saving...</h3>';
          options.showDataSaving('Saving...');

          const surveyData = { ...sender.data };

          sender.getAllQuestions().forEach(question => {
            surveyData[question.valueName || question.name] =
              typeof question.value === 'undefined' || question.value === null ? null : question.value;
          });

          // Transform data for API. The survey locale mirrors the app language
          // (see the locale effect below); SurveyJS reports its default locale
          // as '' and the payload builder records that as 'en'.
          const apiData = buildSurveyPayload(surveyData, showData, source, sender.locale);

          // call the API
          surveyService
            .saveMicrosite(apiData)
            .then(data => {
              sender.completedHtml = originalMesage;
              options.showDataSavingSuccess();
              navigate(`/success`, {
                state: {
                  team_id: showData.teamID,
                  showData: {
                    teamID: showData.teamID,
                    isLincoln: showData.isLincoln,
                    isCanadian: showData.isCanadian,
                    showName: showData.showName,
                  },
                  result: {
                    pin: data.pin,
                    waiverSigned: !!surveyData.signature,
                    minorsCount: countMinors(surveyData),
                  },
                },
              });
            })
            .catch(err => {
              const error = err instanceof AxiosError ? err : createError(err.toString());
              console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                response: error.response,
              });
              sender.completedHtml = '<h3>Error saving survey data</h3>';
              options.showDataSavingError();
            });
        });

        setCurrentPage(survey.currentPageNo);
        setThisSurvey(survey);
      };

      initSurvey();
    }
  }, [showData, resolvedConfig, source, specialist_id, searchParams, navigate]);

  useEffect(() => {
    if (thisSurvey) {
      thisSurvey.locale = language;
      thisSurvey.render();
      // The stepper and page heading read localized page titles during render;
      // this effect runs after that render, so re-render once the locale is set.
      setLocaleVersion(v => v + 1);
    }
  }, [thisSurvey, language]);

  useEffect(() => {
    if (!thisSurvey || !showData) return;

    const handlePageChanged = (sender: Model) => {
      setCurrentPage(sender.currentPageNo);
      // Hide previous button on personInfoCAN page
      sender.showPrevButton = sender.currentPage?.name !== 'personInfoCAN';
    };

    thisSurvey.onCurrentPageChanged.add(handlePageChanged);
    thisSurvey.showPrevButton = thisSurvey.currentPage?.name !== 'personInfoCAN';

    return () => {
      thisSurvey.onCurrentPageChanged.remove(handlePageChanged);
    };
  }, [thisSurvey, showData]);

  useEffect(() => {
    if (thisSurvey) {
      // Expose survey instance to window
      (window as any).survey = thisSurvey;

      // Add custom functions to survey
      thisSurvey.consentAccept = () => {
        thisSurvey.setValue('fordEmailOptIn', 1);
        thisSurvey.nextPage();
      };

      thisSurvey.consentReject = () => {
        if (!showData) return;

        // Show the rejection message overlay for 3 seconds, then return to consent
        setConsentRejected(true);
        setTimeout(() => setConsentRejected(false), 3000);

        // Fire-and-forget: record the abandoned entry for analytics
        surveyService
          .saveMicrosite(buildAbandonedPayload(name => thisSurvey.getValue(name), showData, thisSurvey.locale))
          .catch(err => console.error('Error recording consent rejection:', err));

        // Regenerate the survey GUID so that if the visitor changes their mind
        // and completes the survey, the real registration is INSERTed under a
        // fresh device_survey_guid. Otherwise the API's INSERT IGNORE would
        // silently drop the real row against this abandoned one (same guid),
        // returning 201 with the PIN attached to the ABANDONED registration.
        thisSurvey.setValue('device_survey_guid', uuidv4());
      };
    }
  }, [thisSurvey, showData]);

  if (loading) return <SurveySkeleton />;
  if (error) {
    const isTimeout = error.code === 'ECONNABORTED';
    return (
      <div className="survey-error">
        <h1>{isTimeout ? 'Connection Timeout' : 'Error'}</h1>
        {isTimeout ? (
          <>
            <p>
              The server is taking longer than expected to respond. This could be due to a slow internet connection.
            </p>
            <p>Please check your connection and try again.</p>
          </>
        ) : (
          <p>{error.message || 'An unexpected error occurred'}</p>
        )}
        <Button onPress={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }
  if (!showData || !thisSurvey) return null;

  const brandAttr = showData.isLincoln ? 'lincoln' : 'ford';
  const pageTitle = thisSurvey.currentPage?.title || showData.showName;

  return (
    <>
      {consentRejected && (
        <div className="consent-rejected-overlay" role="status">
          <div className="consent-rejected-overlay__card">
            {language === 'fr' ? 'Merci pour votre temps.' : 'Thank you for your time.'}
          </div>
        </div>
      )}

      <div className="survey-page" data-brand={brandAttr}>
        <div className="survey-column">
          <SurveyStepper
            survey={thisSurvey}
            currentPageIndex={currentPage}
            fallbackLabel={i =>
              t('pageXofY')
                .replace('{0}', String(i + 1))
                .replace('{1}', String(thisSurvey.visiblePages.length))
            }
          />
          <h1 className="survey-page__title">{pageTitle}</h1>
          {/* Host wrapper: `data-brand` is the survey-core host contract — the
              FDS tokens and the SurveyJS-3 adapter key off this attribute. */}
          <div className="meridian-survey-host" data-brand={brandAttr}>
            <SurveyComponent model={thisSurvey} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Survey;
